<template>
    <div>
        <kgr-alert sticky />
        <kgr-loading v-if="loading" />
        <kgr-form-scaffold
            v-else
            :pending="loading"
            :with-back-button="editMealPlan !== null"
            :with-primary-buttons="editable"
            with-print-button
            print-view-hide-buttons
            :save-button-label="editMealPlan === null ? $t('edit') : $t('save')"
            :save-button-disabled="editMealPlan !== null && !changesDetected"
            @primaryBtnClicked="saveOrEdit"
            @backBtnClicked="onCancel"
        >
            <kgr-dropdown
                v-if="editMealPlan === null"
                v-model="mealPlan"
                no-null-option
                :label="$t('Wochenauswahl')"
                inline
                display="range"
                :options="mealPlans"
                option-key="range"
                class="kgr-hidden-print"
                @post-input="onMealPlanChange"
            />
            <h3
                v-if="mealPlan"
                v-t="$t('Speiseplan vom {range}', { range: mealPlan.range })"
                class="kgr-headline kgr-headline--h3 kgr-headline--text"
            ></h3>
            <p
                v-if="editable"
                v-t="'MealPlan.menuInfo'"
                class="kgr-hidden-print kgr-row"
            ></p>

            <kgr-empty-data
                v-if="showEmptyData"
                :value="$t('Sie haben bisher keine PDF-Dateien hochgeladen')"
            />
            <div
                v-else-if="showTable"
                class="kgr-row"
            >
                <kgr-table
                    style="width: 100%"
                    class="kgr-hidden-print"
                    :searching="false"
                >
                    <thead>
                        <tr class="kgr-table__row">
                            <kgr-th>{{ $t('name') }}</kgr-th>
                            <kgr-th>{{ $t('action') }}</kgr-th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="(uploadFile, k) in uploadFiles"
                            :key="`new${k}`"
                            class="kgr-table__row"
                        >
                            <td class="kgr-table__cell">
                                <kgr-text-input v-model="uploadFile.title" />
                            </td>
                            <td class="kgr-table__cell kgr-table__cell--actions">
                                <button
                                    type="button"
                                    class="kgr-table__action"
                                    @click="removeUploadFile(uploadFile)"
                                >
                                    <kgr-icon-delete :title="$t('PDF löschen')" />
                                </button>
                                <span
                                    v-if="invalidFileSize(uploadFile.file)"
                                    class="kgr-text--bold kgr-color--warning"
                                >{{ $t('Diese Datei ist ist zu groß (max. 10 MB)') }}</span>
                                <em
                                    v-else
                                    class="kgr-text--italic kgr-text--small"
                                >{{ $t('MealPlan.file.notYetUploaded') }}</em>
                            </td>
                        </tr>
                        <template v-if="mealPlan">
                            <tr
                                v-for="(file, k) in mealPlan.files"
                                :key="k"
                                class="kgr-table__row"
                                :class="{ 'is-deleted': mealPlan.deleteFiles.find(f => f.id === file.id) != null }"
                            >
                                <td class="kgr-table__cell">
                                    <kgr-text-input
                                        v-if="editMealPlan !== null && mealPlan.deleteFiles.find(f => f.id === file.id) == null"
                                        v-model="file.title"
                                    />
                                    <span v-else>{{ file.title }}</span>
                                </td>
                                <td class="kgr-table__cell kgr-table__cell--actions">
                                    <button
                                        type="button"
                                        class="kgr-table__action"
                                        @click="viewFile(file)"
                                    >
                                        <kgr-icon-preview :title="$t('Datei im Browser betrachten')" />
                                    </button>
                                    <button
                                        type="button"
                                        class="kgr-table__action"
                                        @click="downloadFile(file)"
                                    >
                                        <kgr-icon-download :title="$t('Datei herunterladen')" />
                                    </button>
                                    <button
                                        v-if="editMealPlan !== null && mealPlan.deleteFiles.find(f => f.id === file.id) == null"
                                        type="button"
                                        class="kgr-table__action"
                                        @click="removeFile(file)"
                                    >
                                        <kgr-icon-delete :title="$t('PDF löschen')" />
                                    </button>
                                    <button
                                        v-if="editMealPlan !== null && mealPlan.deleteFiles.find(f => f.id === file.id) != null"
                                        type="button"
                                        class="kgr-table__action"
                                        @click="undoRemoveFile(file)"
                                    >
                                        <kgr-icon-reset :title="$t('Löschen rückgängig machen')" />
                                    </button>
                                </td>
                            </tr>
                        </template>
                    </tbody>
                </kgr-table>
            </div>

            <kgr-drop-file
                v-if="editMealPlan"
                ref="dropFile"
                multiple-extended-files
                pdf-only
                @change="onFileChange"
            />
            <div>
                {{ $t('Sie können ausschließlich PDFs mit einer Größe von bis zu 10 MB hochladen') }}
            </div>

            <kgr-note-box
                v-if="editable && mealPlan && (mealPlan.files.length > 0 || uploadFiles.length > 0 || editMealPlan !== null)"
                class="kgr-hidden-print"
                text-key="MealPlan.file.publicNote.text"
                text-title="MealPlan.file.publicNote.title"
            />
            <hr class="kgr-separator kgr-separator--light mb-2 mt-2" />

            <button
                v-if="!editMealPlan && translateTo"
                style="float: right;"
                class="kgr-button kgr-button--accent kgr-hidden-print"
                @click="showOriginal = !showOriginal"
            >
                {{ showOriginal ? $t('Übersetzung anzeigen') : $t('Originaltext anzeigen') }}
            </button>
            <table
                v-if="mealPlan"
                style="width: auto"
                class="kgr-table kgr-table--block table-dates printMenu"
            >
                <tbody>
                    <tr class="kgr-table__row kgr-table__row--top">
                        <td
                            v-t="'weekday_1'"
                            class="kgr-table__cell kgr-text kgr-text--bold"
                        ></td>
                        <td class="kgr-table__cell kgr-wysiwyg">
                            <span
                                v-if="editMealPlan === null"
                                v-html="weekdaysText.monday"
                            ></span>
                            <kgr-editor-mini
                                v-else
                                v-model="mealPlan.days.monday"
                                editor-id="kgr-editor-monday"
                                height="200"
                                :locale="locale"
                            />
                        </td>
                    </tr>
                    <tr class="kgr-table__row kgr-table__row--top">
                        <td
                            v-t="'weekday_2'"
                            class="kgr-table__cell kgr-text kgr-text--bold"
                        ></td>
                        <td class="kgr-table__cell kgr-wysiwyg">
                            <span
                                v-if="editMealPlan === null"
                                v-html="weekdaysText.tuesday"
                            ></span>
                            <kgr-editor-mini
                                v-else
                                v-model="mealPlan.days.tuesday"
                                editor-id="kgr-editor-tuesday"
                                height="200"
                                :locale="locale"
                            />
                        </td>
                    </tr>
                    <tr class="kgr-table__row kgr-table__row--top">
                        <td
                            v-t="'weekday_3'"
                            class="kgr-table__cell kgr-text kgr-text--bold"
                        ></td>
                        <td class="kgr-table__cell kgr-wysiwyg">
                            <span
                                v-if="editMealPlan === null"
                                v-html="weekdaysText.wednesday"
                            ></span>
                            <kgr-editor-mini
                                v-else
                                v-model="mealPlan.days.wednesday"
                                editor-id="kgr-editor-wednesday"
                                height="200"
                                :locale="locale"
                            />
                        </td>
                    </tr>
                    <tr class="kgr-table__row kgr-table__row--top">
                        <td
                            v-t="'weekday_4'"
                            class="kgr-table__cell kgr-text kgr-text--bold"
                        ></td>
                        <td class="kgr-table__cell kgr-wysiwyg">
                            <span
                                v-if="editMealPlan === null"
                                v-html="weekdaysText.thursday"
                            ></span>
                            <kgr-editor-mini
                                v-else
                                v-model="mealPlan.days.thursday"
                                editor-id="kgr-editor-thursday"
                                height="200"
                                :locale="locale"
                            />
                        </td>
                    </tr>
                    <tr class="kgr-table__row kgr-table__row--top">
                        <td
                            v-t="'weekday_5'"
                            class="kgr-table__cell kgr-text kgr-text--bold"
                        ></td>
                        <td class="kgr-table__cell kgr-wysiwyg">
                            <span
                                v-if="editMealPlan === null"
                                v-html="weekdaysText.friday"
                            ></span>
                            <kgr-editor-mini
                                v-else
                                v-model="mealPlan.days.friday"
                                editor-id="kgr-editor-friday"
                                height="200"
                                :locale="locale"
                            />
                        </td>
                    </tr>
                </tbody>
            </table>
        </kgr-form-scaffold>
    </div>
</template>

<script>
import Date from '../../../js/date/Date.js';
import DropFile from '../../common/form/input/DropFile.vue';
import Dropdown from '../../common/form/select/Dropdown.vue';
import EmptyData from '../../common/EmptyData.vue';
import FormScaffold from '../../common/form/FormScaffold.vue';
import IconDelete from '../../common/icon/IconDelete.vue';
import IconDownload from '../../common/icon/IconDownload.vue';
import IconPreview from '../../common/icon/IconPreview.vue';
import IconReset from '../../common/icon/IconReset.vue';
import Loading from '../../common/Loading.vue';
import MealPlan from '../../../js/dto/MealPlan.js';
import MealPlanRepository from '../../../js/repository/MealPlanRepository.js';
import NoteBox from '../../common/NoteBox.vue';
import TextInput from '../../common/form/text-input/TextInput.vue';
import WysiwygEditorMini from '../../WysiwygEditorMini.vue';
import axios from 'axios';
import { getLanguageFromCookie } from '../../../js/service/LanguageHelper.js';

export default {
    components: {
        'kgr-drop-file': DropFile,
        'kgr-icon-reset': IconReset,
        'kgr-icon-delete': IconDelete,
        'kgr-icon-download': IconDownload,
        'kgr-icon-preview': IconPreview,
        'kgr-text-input': TextInput,
        'kgr-empty-data': EmptyData,
        'kgr-loading': Loading,
        'kgr-editor-mini': WysiwygEditorMini,
        'kgr-form-scaffold': FormScaffold,
        'kgr-dropdown': Dropdown,
        'kgr-note-box': NoteBox,
    },
    props: {
        kindergartenId: {
            type: Number,
            required: true,
        },
        locale: {
            type: String,
            required: false,
            default: 'de_DE',
        },
        editable: {
            type: Boolean,
            required: true,
        },
    },
    data() {
        return {
            start: Date.today().subDays(52 * 7).mondayOfWeek(),
            end: Date.today().addDays(8 * 7).mondayOfWeek(),
            uploadFiles: [],
            originData: null,
            maxFileSize: 10000000,
            loading: true,
            mealPlans: null,
            mealPlan: null,
            editMealPlan: null,
            showOriginal: false,
            translateTo: null,
            days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
        };
    },
    computed: {
        showTable() {
            return this.uploadFiles.length > 0 || this.mealPlan?.files.length > 0;
        },
        showEmptyData() {
            return this.uploadFiles.length === 0 && this.mealPlan?.files.length === 0 && this.editMealPlan !== null;
        },
        changesDetected() {
            return this.uploadFiles.length > 0 || this.mealPlan?.deleteFiles.length > 0 || this.originData !== this.buildData(this.mealPlan);
        },
        weekdaysText() {
            const result = {};

            this.days.forEach((day) => {
                if (!this.showOriginal && this.translateTo && this.mealPlan.translations?.days[day]) {
                    result[day] = this.mealPlan.translations.days[day];
                } else {
                    result[day] = this.mealPlan.days[day];
                }
            });

            return result;
        },
        untranslatedMealPlans() {
            if (!this.translateTo) {
                return [];
            }
            return this.mealPlans.filter((mealPlan) => {
                return this.days.some((day) => {
                    const hasText = !!mealPlan.days?.[day];
                    const hasTranslation = !!mealPlan.translations?.days?.[day];
                    return hasText && !hasTranslation;
                });
            });
        },
    },
    watch: {
        mealPlan(value) {
            if (!value) {
                return;
            }
            window.location.hash = value.start.toIso8601();
        },
    },
    created() {
        const savedLang = getLanguageFromCookie();
        if (savedLang && savedLang !== 'de_DE') {
            this.translateTo = savedLang;
        }
        this.loadMealPlans();
    },
    methods: {
        async triggerTranslations() {
            const untranslated = this.untranslatedMealPlans;
            if (untranslated.length === 0) {
                return;
            }

            try {
                this.loading = true;
                const mealPlanIds = untranslated.map((mp) => mp.id).filter(Boolean);

                await axios.post(`/kindergartens/${this.kindergartenId}/meal-plans/translations`, {
                    ids: mealPlanIds,
                    translationLanguage: this.translateTo,
                });

                await this.loadMealPlans();
            } catch (error) {
                this.$notify.error('Beim Übersetzen ist ein Fehler aufgetreten.');
            } finally {
                this.loading = false;
            }
        },
        async loadMealPlans() {
            MealPlanRepository.findByKindergarten(this.kindergartenId, { start: this.start, end: this.end })
                .then((mealPlans) => {
                    this.mealPlans = this.fillMealPlans(mealPlans);
                    const date = this.validUrlDate() || Date.today();
                    this.mealPlan = this.mealPlans.filter((mp) => mp.start.isBeforeOrEqual(date)).pop();
                    window.location.hash = this.mealPlan.start.toIso8601();
                    this.initOriginData();
                    this.loading = false;
                    if (this.untranslatedMealPlans.length > 0) {
                        this.triggerTranslations();
                    }
                });
        },
        fillMealPlans(mealPlans) {
            const allMealPlans = [];
            let loopDate = this.start.clone();
            while (loopDate.isBeforeOrEqual(this.end)) {
                allMealPlans.push(mealPlans.find((mp) => mp.start.equals(loopDate)) || MealPlan.createEmpty(loopDate.clone()));
                loopDate = loopDate.addDays(7);
            }
            return allMealPlans;
        },
        validUrlDate() {
            try {
                const date = Date.fromIso8601(window.location.hash.substr(1));
                return this.mealPlans.find((mp) => mp.start.equals(date)) != null ? date : null;
            } catch (error) {
                return null;
            }
        },
        onMealPlanChange(mealPlan) {
            this.uploadFiles = [];
            mealPlan.deleteFiles = [];
            this.initOriginData();
            if (this.editMealPlan !== null) {
                this.editMealPlan = mealPlan;
            }
        },
        invalidFileSize(file) {
            return file.size > this.maxFileSize;
        },
        async onCancel() {
            await this.loadMealPlans();
            this.initData(this.editMealPlan.id);
        },
        viewFile(file) {
            window.open(`/api/meal-plan-files/${file.id}?inline`, '_blank');
        },
        downloadFile(file) {
            document.location = `/api/meal-plan-files/${file.id}`;
        },
        removeFile(file) {
            this.editMealPlan.deleteFiles.push(file);
        },
        removeUploadFile(uploadFile) {
            this.uploadFiles = this.uploadFiles.filter((f) => f !== uploadFile);
        },
        undoRemoveFile(file) {
            this.editMealPlan.deleteFiles = this.editMealPlan.deleteFiles.filter((f) => f.id !== file.id);
        },
        onFileChange(uploadFiles) {
            uploadFiles.forEach((uploadFile) => {
                this.uploadFiles.push({
                    file: uploadFile,
                    title: uploadFile.name,
                    data: uploadFile.data,
                });
            });
            this.$refs.dropFile.resetFiles();
        },
        buildData(mealPlan) {
            return JSON.stringify({ ...mealPlan?.days, ...mealPlan?.files });
        },
        initOriginData() {
            this.originData = this.buildData(this.mealPlan);
        },
        initData(selected) {
            this.mealPlan = this.mealPlans.find((mp) => mp.id === selected);
            this.editMealPlan = null;
            this.initOriginData();
            this.uploadFiles = [];
        },
        async saveOrEdit() {
            if (this.editMealPlan === null) {
                this.editMealPlan = this.mealPlan;
                return;
            }
            const validUploadFiles = this.uploadFiles.filter((f) => f.file.size <= this.maxFileSize);
            this.loading = true;
            try {
                let id = this.editMealPlan.id;

                if (!id) {
                    id = await MealPlanRepository.add(this.kindergartenId, this.editMealPlan);
                    if (validUploadFiles.length > 0) {
                        await MealPlanRepository.addFiles(id, validUploadFiles);
                    }
                } else {
                    await MealPlanRepository.update(this.editMealPlan);
                    if (validUploadFiles.length > 0) {
                        await MealPlanRepository.addFiles(id, validUploadFiles);
                    }
                }

                await this.loadMealPlans();
                this.initData(id);

                this.$notify.success('saveChanges.success');
            } catch {
                this.$notify.error('saveChanges.error');
            } finally {
                this.loading = false;
            }
        },
    },
};
</script>

<style rel="stylesheet/scss" lang="scss" scoped>
</style>
